import { useState } from "react";
import styled from "styled-components";
import { toast } from "react-toastify";
import { DatePicker, InputMoney, InputTextarea, ModalForm, Notice, unMask } from "@ficatranquilo/contabilidade.com-ui";
import useService from "../../hooks/useService";
import { isoToDate, localIsoDate } from "../../helpers/localDate";
import { formatCompetencia, moneyInputText } from "../ow";

const Content = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

// Vencimento e valor lado a lado: o modal precisa caber em telas de notebook (~650px úteis)
const Fields = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
`;

/**
 * Edição de um cronograma (vencimento, valor esperado e observações): na aba
 * Pagamentos do contrato e no detalhe do pagamento. Só para cronograma pendente e
 * sem nota (quem abre confere).
 *
 * Props:
 * - schedule: cronograma como vem da API
 * - isHourly: contrato por hora (mostra o aviso do recálculo)
 * - onClose
 * - onSaved: depois de salvar (recarregar)
 */
export default function EditScheduleModal({ schedule, isHourly, onClose, onSaved }) {
  const paymentScheduleService = useService("payment-schedules");
  const [form, setForm] = useState(() => ({
    due_date: schedule.due_date ? schedule.due_date.split("T")[0] : "",
    expected_value: moneyInputText(schedule.expected_value),
    notes: schedule.notes || "",
  }));
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  function close() {
    if (!loading) onClose();
  }

  async function confirm() {
    if (!form.due_date) {
      setErrors({ due_date: "Informe a data de vencimento" });
      return;
    }
    setLoading(true);
    try {
      const payload = {
        due_date: form.due_date,
        notes: form.notes,
      };
      if (form.expected_value !== "") {
        payload.expected_value = parseFloat(unMask(form.expected_value, true));
      }
      const result = await paymentScheduleService.updateSchedule(schedule.id, payload);
      if (result && !result.error) {
        toast.success(result.msg || "Cronograma atualizado!");
        setLoading(false);
        onSaved();
        return;
      }
      // Erro com { error: true } vem do sendError do service, que já mostrou o toast
      if (!result?.error) toast.error("Erro ao atualizar cronograma");
    } catch (err) {
      toast.error("Erro ao atualizar cronograma");
    }
    setLoading(false);
  }

  const competence = { month: Number(schedule.reference_month), year: Number(schedule.reference_year) };

  return (
    <ModalForm
      $showModal
      $setShowModal={close}
      $noClose={loading}
      $isLoading={loading}
      $size="medium"
      $title={`Editar cronograma de ${formatCompetencia(competence, true)}`}
      $contentBackground="N0"
      $actions={[
        { text: "Cancelar", action: close, variant: "outline", isDisabled: loading },
        { text: "Salvar alterações", action: confirm, variant: "primary", isDisabled: loading },
      ]}>
      <Content>
        <Fields>
          <DatePicker
            $idForm="edit-due-date"
            $label="Data de vencimento"
            $variant="default"
            $isRequired
            $errors={errors.due_date}
            $date={isoToDate(form.due_date)}
            $setDate={(date) => {
              setForm((prev) => ({ ...prev, due_date: localIsoDate(date) || "" }));
              if (errors.due_date) setErrors({});
            }}
          />
          <InputMoney
            $idForm="edit-expected-value"
            $label="Valor esperado"
            $variant="default"
            placeholder="R$ 0,00"
            inputMode="numeric"
            value={form.expected_value}
            onChange={(e) => setForm((prev) => ({ ...prev, expected_value: e.target.value }))}
          />
        </Fields>
        {isHourly ? (
          <Notice
            $variant="orange"
            $text="Contrato por hora: o valor informado aqui pode ser substituído quando horas do período forem aprovadas ou ao usar <strong>Recalcular horas</strong>."
          />
        ) : null}
        <InputTextarea
          $idForm="edit-notes"
          $label="Observações"
          $variant="default"
          rows={2}
          value={form.notes}
          onChange={(e) => setForm((prev) => ({ ...prev, notes: e.target.value }))}
          placeholder="Observações (opcional)"
        />
      </Content>
    </ModalForm>
  );
}
